import Cart from "cart-store";
import { loadBundleDeals, whenCartDrawerOpens } from "cart-upsell-data";

const SOURCE = "product_variants";
const MAX_COLORS = 3;
const MIN_COLORS = 3;
const RECAP_DURATION_MS = 4500;
const productCache = new Map();

function formatEuro(cents) {
  const value = (cents / 100).toFixed(2).replace(".", ",");
  return `€${value}`;
}

class CartVariantUpsell extends HTMLElement {
  connectedCallback() {
    this.lifecycle = new AbortController();
    whenCartDrawerOpens(this, () => this.#activate(this.lifecycle.signal), this.lifecycle.signal);
  }

  /* Deal data and product lookups wait until the drawer opens, so closed-drawer page views cost nothing. */
  async #activate(signal) {
    this.bundleDeals = await loadBundleDeals();
    if (signal.aborted || this.unsubscribe) return;

    this.grid = this.querySelector("[data-options]");
    this.headerTitle = this.querySelector(".cart-variant-upsell__title");
    this.progressEl = this.querySelector("[data-progress]");
    this.companionId = this.dataset.companionId;
    this.i18n = {
      add: this.dataset.i18nAdd || "Add",
      size: this.dataset.i18nSize || "Size",
      progress: this.dataset.i18nProgress || "Add 1 more → save <strong>__AMOUNT__</strong>",
      recap: this.dataset.i18nRecap || "🎉 You save <strong>__AMOUNT__</strong>!",
    };
    this.isVisible = false;
    this.shownEmitted = false;
    this.currentHandle = null;
    this.currentTier = null;
    this.currentProductId = null;
    this.fetchToken = 0;
    this.recapTimer = null;
    this.recapShowing = false;
    this.lastUnitPrice = 0;
    this.lastDeal = null;

    this.#evaluate(Cart.getState().cart);
    this.unsubscribe = Cart.subscribe((state) => this.#evaluate(state.cart));
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    this.unsubscribe?.();
    this.unsubscribe = null;
    if (this.recapTimer) clearTimeout(this.recapTimer);
  }

  /**
   * Tier identity used for pairing. The discount app pairs any two items that
   * share a "2 voor X" amount, so the amount — not the product handle — is what
   * decides whether a deal is already complete. bundlePrice is a stand-in in
   * case an older bundle-deals-data island omits `amount`.
   */
  #dealTierKey(handle) {
    const deal = this.bundleDeals[handle];
    if (!deal) return null;
    return String(deal.amount ?? deal.bundlePrice);
  }

  #tierQty(items, tierKey) {
    return items.reduce(
      (n, i) =>
        this.#dealTierKey(i.handle) === tierKey ? n + (i.quantity || 0) : n,
      0,
    );
  }

  /**
   * A tier only has something left to upsell when its total quantity is odd —
   * every even count is already fully paired off by the discount app. Anchors
   * on the last item of that tier in cart order, i.e. the one just added.
   */
  #findUnpairedTier(items) {
    const qtyByTier = new Map();
    for (const i of items) {
      const key = this.#dealTierKey(i.handle);
      if (!key) continue;
      qtyByTier.set(key, (qtyByTier.get(key) || 0) + (i.quantity || 0));
    }

    for (const [key, qty] of qtyByTier) {
      if (qty % 2 === 0) continue;
      for (let idx = items.length - 1; idx >= 0; idx--) {
        const item = items[idx];
        if (this.#dealTierKey(item.handle) !== key) continue;
        return { item, deal: this.bundleDeals[item.handle], key };
      }
    }
    return null;
  }

  async #evaluate(cart) {
    const items = cart?.items || [];
    const unpaired = this.#findUnpairedTier(items);

    if (!unpaired) {
      if (this.isVisible && !this.recapShowing && this.lastDeal && this.currentTier) {
        if (this.#tierQty(items, this.currentTier) >= 2) {
          this.#emit("second_item_added", {
            variant_id: items[items.length - 1]?.variant_id ?? null,
          });
          this.#showRecap();
          return;
        }
      }
      if (!this.recapShowing) {
        this.#setVisible(false);
        this.shownEmitted = false;
        this.currentHandle = null;
        this.currentTier = null;
      }
      return;
    }

    const { item, deal, key: tierKey } = unpaired;
    const handle = item.handle;
    const productId = String(item.product_id);
    const currentVariantId = item.variant_id;
    const unitPrice = item.original_price ?? item.price ?? 0;

    this.lastDeal = deal;
    this.lastUnitPrice = unitPrice;
    this.currentTier = tierKey;

    if (this.currentHandle === handle && this.isVisible) {
      this.#updateProgress(unitPrice, deal);
      return;
    }
    this.currentHandle = handle;
    this.currentProductId = productId;

    const token = ++this.fetchToken;
    let product;
    try {
      product = await this.#fetchProduct(handle);
    } catch {
      this.#setVisible(false);
      return;
    }
    if (token !== this.fetchToken) return;

    const tiles = this.#buildTiles(product, currentVariantId);
    if (tiles.length < MIN_COLORS) {
      this.#setVisible(false);
      return;
    }

    this.tiles = tiles;
    this.tileState = new Map();
    this.hasSize = product.options.some((o) =>
      /size/i.test(typeof o === "string" ? o : o?.name || ""),
    );
    this.productTitle = product.title;
    this.#renderTiles();
    this.#updateProgress(unitPrice, deal);
    this.#setVisible(true);

    if (!this.shownEmitted) {
      this.shownEmitted = true;
      this.#emit("upsell_shown", { variant_id: null });
    }
  }

  #updateProgress(unitPrice, deal) {
    if (!this.progressEl || !deal) return;
    const savings = unitPrice * 2 - deal.bundlePrice;
    if (savings <= 0) {
      this.progressEl.hidden = true;
      return;
    }
    this.progressEl.hidden = false;
    this.progressEl.classList.remove("is-recap");
    this.progressEl.innerHTML = this.i18n.progress.replaceAll("__AMOUNT__", formatEuro(savings));
  }

  #showRecap() {
    if (!this.lastDeal) return;
    const savings = this.lastUnitPrice * 2 - this.lastDeal.bundlePrice;
    if (savings <= 0) {
      this.#setVisible(false);
      return;
    }
    this.recapShowing = true;
    if (this.grid) this.grid.style.display = "none";
    if (this.progressEl) {
      this.progressEl.hidden = false;
      this.progressEl.classList.add("is-recap");
      this.progressEl.innerHTML = this.i18n.recap.replaceAll("__AMOUNT__", formatEuro(savings));
    }
    if (this.headerTitle) this.headerTitle.hidden = true;
    this.#setVisible(true);

    if (this.recapTimer) clearTimeout(this.recapTimer);
    this.recapTimer = setTimeout(() => {
      this.recapShowing = false;
      this.recapTimer = null;
      if (this.grid) this.grid.style.display = "";
      if (this.headerTitle) this.headerTitle.hidden = false;
      if (this.progressEl) this.progressEl.classList.remove("is-recap");
      this.#setVisible(false);
      this.shownEmitted = false;
      this.currentHandle = null;
      this.currentTier = null;
      this.lastDeal = null;
    }, RECAP_DURATION_MS);
  }

  async #fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const res = await fetch(
      `${window.Shopify?.routes?.root ?? "/"}products/${handle}.js`,
    );
    if (!res.ok) throw new Error(`Product fetch failed: ${res.status}`);
    const data = await res.json();
    productCache.set(handle, data);
    return data;
  }

  #buildTiles(product, currentVariantId) {
    const optionName = (o) => (typeof o === "string" ? o : o?.name || "");
    const colorOptionIdx = product.options.findIndex((o) =>
      /colou?r/i.test(optionName(o)),
    );
    if (colorOptionIdx === -1) return [];
    const sizeOptionIdx = product.options.findIndex((o) =>
      /size/i.test(optionName(o)),
    );

    const currentVariant = product.variants.find(
      (v) => v.id === currentVariantId,
    );
    const currentColor = currentVariant
      ? currentVariant[`option${colorOptionIdx + 1}`]
      : null;

    const byColor = new Map();
    for (const v of product.variants) {
      const color = v[`option${colorOptionIdx + 1}`];
      if (!color || color === currentColor) continue;
      if (!byColor.has(color)) byColor.set(color, []);
      byColor.get(color).push(v);
    }

    const tiles = [];
    for (const [color, variants] of byColor) {
      const anyAvailable = variants.some((v) => v.available);
      if (!anyAvailable) continue;

      const firstWithImage = variants.find((v) => v.featured_image?.src);
      const image =
        firstWithImage?.featured_image?.src || product.featured_image || null;

      tiles.push({
        color,
        image,
        sizes: variants.map((v) => ({
          id: v.id,
          label: sizeOptionIdx >= 0 ? v[`option${sizeOptionIdx + 1}`] : "",
          available: v.available,
        })),
      });

      if (tiles.length >= MAX_COLORS) break;
    }
    return tiles;
  }

  #setVisible(show) {
    if (this.isVisible === show) return;
    this.isVisible = show;
    if (show) {
      this.removeAttribute("hidden");
      this.style.display = "";
    } else {
      this.setAttribute("hidden", "");
      this.style.display = "none";
    }
    this.#hideCompanion(show);
  }

  #renderTiles() {
    const frag = document.createDocumentFragment();
    this.tiles.forEach((tile, i) => {
      const li = document.createElement("li");
      li.className = "cart-variant-upsell__item";

      const single = tile.sizes.length === 1;
      const defaultSize = single
        ? tile.sizes[0]
        : tile.sizes.find((s) => s.available) || tile.sizes[0];
      this.tileState.set(i, { selectedId: defaultSize?.id ?? null });

      li.innerHTML = `
        ${tile.image ? `<img class="cart-variant-upsell__img" src="${tile.image}" alt="" loading="lazy" width="120" height="120">` : ""}
        <span class="cart-variant-upsell__color">${escapeHtml(tile.color)}</span>
        ${
          this.hasSize && !single
            ? `<div class="cart-variant-upsell__sizes" role="radiogroup" aria-label="${escapeHtml(this.i18n.size)}">
                ${tile.sizes
                  .map(
                    (s) =>
                      `<button type="button" class="cart-variant-upsell__size${s.id === defaultSize?.id ? " is-selected" : ""}${s.available ? "" : " is-unavailable"}" data-variant-id="${s.id}" ${s.available ? "" : "disabled"} aria-pressed="${s.id === defaultSize?.id}">${escapeHtml(s.label)}</button>`,
                  )
                  .join("")}
              </div>`
            : ""
        }
        <button type="button" class="cart-variant-upsell__add" data-add ${defaultSize?.available ? "" : "disabled"}>${escapeHtml(this.i18n.add)}</button>
      `;

      const sizeBtns = li.querySelectorAll(".cart-variant-upsell__size");
      sizeBtns.forEach((btn) => {
        btn.addEventListener("click", () => {
          if (btn.disabled) return;
          sizeBtns.forEach((b) => {
            b.classList.remove("is-selected");
            b.setAttribute("aria-pressed", "false");
          });
          btn.classList.add("is-selected");
          btn.setAttribute("aria-pressed", "true");
          this.tileState.set(i, { selectedId: Number(btn.dataset.variantId) });
          const addBtn = li.querySelector("[data-add]");
          if (addBtn) addBtn.disabled = false;
        });
      });

      const addBtn = li.querySelector("[data-add]");
      addBtn.addEventListener("click", () => this.#addSelected(i, addBtn));

      frag.appendChild(li);
    });
    this.grid.replaceChildren(frag);
  }

  async #addSelected(tileIndex, btn) {
    const state = this.tileState.get(tileIndex);
    const variantId = state?.selectedId;
    if (!variantId || btn.disabled) return;
    btn.disabled = true;
    btn.classList.add("is-loading");
    this.#emit("upsell_click", { variant_id: variantId });
    try {
      await Cart.add({ id: variantId, quantity: 1 });
    } catch {
      // cart store dispatches its own on:cart:failed
    } finally {
      btn.disabled = false;
      btn.classList.remove("is-loading");
    }
  }

  #hideCompanion(active) {
    if (!this.companionId) return;
    const el = document.getElementById(`${this.companionId}--recommendations`);
    if (el) el.style.display = active ? "none" : "";
  }

  #emit(name, extra) {
    const detail = {
      product_id: this.currentProductId,
      variant_id: extra?.variant_id ?? null,
      source: SOURCE,
    };
    document.dispatchEvent(new CustomEvent(`upsell:${name}`, { detail }));
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...detail });
    }
  }
}

function escapeHtml(s) {
  return String(s).replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  );
}

if (!customElements.get("cart-variant-upsell")) {
  customElements.define("cart-variant-upsell", CartVariantUpsell);
}
